/* eslint-disable max-classes-per-file */
import { makeObservable, observable } from 'mobx';

/**
 * One rendered signal on a lead, tied back to the definition that produced it.
 *
 * Carried alongside the flat `signals` string rather than replacing it: the
 * string is what a CRM receives and what the column has always shown, while this
 * is what lets the sample offer to go and change the definition.
 */
export interface SignalTrace {
  /** The rendered line, exactly as it appears in `signals`. */
  text: string;
  /** Position of the signal group in the model's own list. */
  group: number;
  /** Position of the matching definition within that group. */
  rule: number;
}

export class SampleLead {
  email: string;

  score: number;

  segment: string;

  signals: string;

  /**
   * One entry per line of `signals`, in the same order, so index N describes the
   * Nth rendered line. `null` where the row carried no usable attribution: the
   * line is then shown without a link, never linked to the wrong definition.
   *
   * Absent entirely on a model whose signals are off, or when no line on the
   * lead could be attributed at all.
   */
  // `declare` on purpose: tsconfig targets ES2022, so useDefineForClassFields is
  // on and a plain declaration would emit a property initialised to undefined on
  // every lead. The key would then always exist, which breaks `deep.equal` against
  // a lead that has no traces and contradicts the comment above. `declare` emits
  // nothing, so the property appears only when it is assigned. Safe because this
  // field is not passed to makeObservable.
  declare signalTraces?: (SignalTrace | null)[];

  hasConverted: boolean;

  constructor(
    email: string,
    score: number,
    segment: string,
    signals: string,
    hasConverted: boolean
  ) {
    this.email = email;
    this.score = score;
    this.segment = segment;
    this.signals = signals;
    this.hasConverted = hasConverted;
  }
}

export class CustomerFitSampleLead extends SampleLead {
  employees: number;

  amountRaised: number;

  marketCap: number;

  isFortune500Company: boolean;

  country: string;

  predictiveTraffic: number;

  industry: string;

  hasEnterpriseTechnology: boolean;

  tree1Node: string;

  tree2Node: string;

  tree3Node: string;

  constructor(
    email: string,
    score: number,
    segment: string,
    signals: string,
    hasConverted: boolean,
    employees: number,
    amountRaised: number,
    marketCap: number,
    isFortune500Company: boolean,
    country: string,
    predictiveTraffic: number,
    industry: string,
    hasEnterpriseTechnology: boolean,
    tree1Node: string,
    tree2Node: string,
    tree3Node: string
  ) {
    super(email, score, segment, signals, hasConverted);
    this.employees = employees;
    this.amountRaised = amountRaised;
    this.marketCap = marketCap;
    this.isFortune500Company = isFortune500Company;
    this.country = country;
    this.predictiveTraffic = predictiveTraffic;
    this.industry = industry;
    this.hasEnterpriseTechnology = hasEnterpriseTechnology;
    this.tree1Node = tree1Node;
    this.tree2Node = tree2Node;
    this.tree3Node = tree3Node;

    makeObservable(this, {
      hasConverted: observable,
      employees: observable,
      amountRaised: observable,
      marketCap: observable,
      isFortune500Company: observable,
      country: observable,
      predictiveTraffic: observable,
      industry: observable,
      hasEnterpriseTechnology: observable,
      tree1Node: observable,
      tree2Node: observable,
      tree3Node: observable,
    });
  }
}

/**
 * Rebuilds a scored lead from the plain object the API returns.
 *
 * The constructor is positional and names sixteen fields, so anything it does
 * not name is dropped unless it is copied across here. That is a standing
 * hazard rather than a one-off: this function exists so the copying has one
 * home and can be tested, instead of sitting inline in a store method behind a
 * network call.
 *
 * `signalTraces` is assigned only when the source has it. Assigning it
 * unconditionally would put the key on every lead with the value `undefined`,
 * which is the thing `declare` on the field is there to avoid: leads would
 * then disagree about whether the key exists depending on which path built
 * them.
 */
export function reviveCustomerFitLead(
  lead: CustomerFitSampleLead
): CustomerFitSampleLead {
  const revived = new CustomerFitSampleLead(
    lead.email,
    lead.score,
    lead.segment,
    lead.signals,
    lead.hasConverted,
    lead.employees,
    lead.amountRaised,
    lead.marketCap,
    lead.isFortune500Company,
    lead.country,
    lead.predictiveTraffic,
    lead.industry,
    lead.hasEnterpriseTechnology,
    lead.tree1Node,
    lead.tree2Node,
    lead.tree3Node
  );
  if (lead.signalTraces) revived.signalTraces = lead.signalTraces;
  return revived;
}

export class BehavioralSampleLead extends SampleLead {
  domain: string;

  constructor(
    email: string,
    score: number,
    segment: string,
    signals: string,
    hasConverted: boolean,
    domain: string
  ) {
    super(email, score, segment, signals, hasConverted);
    this.domain = domain;
    makeObservable(this, {
      domain: observable,
    });
  }
}
